import * as React from 'react';
import { Col, Row, Alert, Button, Radio, Divider, Tabs, Form } from 'antd';
import { observer } from 'mobx-react';
import { Prompt } from 'react-router';
import * as uuid from 'uuid';

import { TextView } from 'src/core/ui/text.view';
import { CardController } from 'src/app/shared/card.controller';
import { HeaderView } from 'src/app/shared/header.view';
import { BooleanView } from 'src/core/ui/boolean.view';
import { DataTableView, ToolbarVisibility, Column } from 'src/core/ui/data-table.view';
import { ProductTypeEditorController } from 'src/app/products/products/parts/product-type-editor.controller';
import { ProductCategoryEditorController } from 'src/app/products/product-categories/parts/product-category-editor.controller';
import { DataLocalService } from 'src/core/services/data-local.service';
import { DropdownView } from 'src/core/ui/dropdown.view';

const Group = Radio.Group;
const TabPane = Tabs.TabPane;

interface ConfigOptionFormViewProps extends ComponentViewProps<DataSet<ConfigOptionItem>> {
	mode: ControlMode;
	onModeChanged: (mode: ControlMode) => void;
	save: () => void;
	reboot: () => void;
	downloadLogs: () => void;
	cancel: () => void;
	onImageChange: (ev: React.ChangeEvent<HTMLInputElement>, field: string, size: number) => void;
	add: (key: string[]) => void;
	logs: { date: Date, message: string, level: number }[];
	resetCacheConnection: () => void;
}
interface ConfigOptionState {
	autoConnetDataset: DataLocalService<AutoConnetProductConfig>
}
@observer
export class ConfigOptionFormView extends React.Component<ConfigOptionFormViewProps, ConfigOptionState> {
	constructor(props: Readonly<ConfigOptionFormViewProps>) {
		super(props);
		let autoconetproductsConfig = this.props.model.items.find(x => x.key == "AutoConnectProducts");
		let autoconetproducts: AutoConnetProductConfig[] = [];
		if (autoconetproductsConfig) {
			let value = JSON.parse(autoconetproductsConfig.value);
			if (value.products.length > 0) {
				autoconetproducts = value.products;
			}
			this.state = {
				autoConnetDataset: new DataLocalService(autoconetproducts)
			};
		}

	}

	private renderImages(label: string, data: string, key: string, size: number) {
		return (
			<React.Fragment>
				<label>{label}</label>
				<label htmlFor={key} className={`label-image text-center`}>
					<img src={'data:image/png;base64,' + data} alt="Select an image" />
					<input
						id={key}
						disabled={this.props.mode == 'display'}
						type="file"
						style={{ display: 'none' }}
						accept="image/*"
						onChange={(ev) => {
							this.props.onImageChange(ev, key, size);
						}}
					/>
				</label>
			</React.Fragment>
		);
	}

	private newAutoConnetProductConfig = () => {
		const { autoConnetDataset } = this.state
		return autoConnetDataset
			.create(() => ({
				id: uuid.v4(),
				productType: null,
				categories: []
			}))
			.then(item => {
				let items = [...autoConnetDataset.items];
				let autoconetproductsConfig = this.props.model.items.find(x => x.key == "AutoConnectProducts");
				if (autoconetproductsConfig)
					autoconetproductsConfig.value = JSON.stringify({ products: items })
				this.setState({
					autoConnetDataset: new DataLocalService(items),
				});
				return item;
			});
	}
	renderAutoConnectProduct() {
		const { autoConnetDataset } = this.state
		const optionsType = [
			{ value: 0, label: 'General' },
			{ value: 1, label: 'Accomodation' },
			{ value: 2, label: 'Car Rent' },
			{ value: 3, label: 'Transfer' },
			{ value: 4, label: 'Flight' },
			{ value: 5, label: 'Room' }
		];
		let columns: Column[] = [
			{
				key: 'productType',
				dataIndex: 'productType',
				title: 'Product Type',
				editable: (text, record: AutoConnetProductConfig, index, isSelected: boolean) => {
					return <ProductTypeEditorController
						label=''
						mode={isSelected ? 'edit' : "display"}
						onChanged={(value) => {
							record.productType = value;
							let autoconetproductsConfig = this.props.model.items.find(x => x.key == "AutoConnectProducts");
							if (autoconetproductsConfig)
								autoconetproductsConfig.value = JSON.stringify({ products: autoConnetDataset.items })
						}}
						value={record.productType}
						setBusy={this.props.setBusy}
						options={optionsType}
					/>
				}
			},
			{
				key: 'categories',
				dataIndex: 'categories',
				title: 'Categories',
				editable: (text, record: AutoConnetProductConfig, index, isSelected: boolean) => {
					return <ProductCategoryEditorController
						label=''
						mode={record.productType != null && isSelected ? 'edit' : 'display'}
						multiOnChange={(values) => {
							record.categories = values.map(c => c.id);
							let autoconetproductsConfig = this.props.model.items.find(x => x.key == "AutoConnectProducts");
							if (autoconetproductsConfig)
								autoconetproductsConfig.value = JSON.stringify({ products: autoConnetDataset.items })
						}}
						value={record.categories}
						multi={true}
						productType={record.productType == 1 ? [1, 5] : record.productType}
						setBusy={this.props.setBusy}
					/>
				}
			}

		];
		return <React.Fragment>
			<label>AutoConnect Products </label>
			<DataTableView
				columns={columns}
				mode={this.props.mode}
				setBusy={this.props.setBusy}
				model={autoConnetDataset as DataSet<AutoConnetProductConfig>}
				createNewItem={this.newAutoConnetProductConfig}
				afterDeleteItem={(items: AutoConnetProductConfig[]) => {
					let current = autoConnetDataset.items.filter(a => !items.some(i => i.id == a.id));
					let autoconetproductsConfig = this.props.model.items.find(x => x.key == "AutoConnectProducts");
					if (autoconetproductsConfig)
						autoconetproductsConfig.value = JSON.stringify({ products: current })
					this.setState({
						autoConnetDataset: new DataLocalService(current),
					});

				}}
				hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hidePagination}
			/>
		</React.Fragment>
	}

	private renderNotifications() {
		const notificationConfig = this.props.model.items.find(x => x.key == 'UsersCanChangeNotificationSettings');
		const frontend = this.props.model.items.find(x => x.key == 'FrontendHostUrl');

		return (
			<CardController
				className="card-borderless"
				mode={this.props.mode}
				icon="user"
				title="User task notification">
				<Row gutter={16}>

					<Col xs={24}>

						{notificationConfig != null && <BooleanView
							mode={this.props.mode}
							label="Allow users to change notification receiving settings"
							value={notificationConfig.value === "true"}
							onChanged={(val) => {
								notificationConfig.value = val.toString();
							}}
						/>
						}
					</Col>
				</Row>
				<Row gutter={24}>
					{frontend && <Col xs={24}>
						<TextView
							label="Frontend host url"
							mode={this.props.mode}
							value={frontend.value}
							onChanged={
								(val) => {
									frontend.value = val;
								}
							}
						/>
					</Col>}
				</Row>
			</CardController>
		);
	}

	private renderIdentity() {
		const logoConfig = this.props.model.items.find(x => x.key == 'Logo');
		const logo = logoConfig ? logoConfig.value : undefined;
		const headerConfig = this.props.model.items.find(x => x.key == 'HeaderDoc');
		const header = headerConfig ? headerConfig.value : undefined;
		const footerConfig = this.props.model.items.find(x => x.key == 'FooterDoc');
		const footer = footerConfig ? footerConfig.value : undefined;
		const useAllotmentBeforeBookingsConfig = this.props.model.items.find(x => x.key == 'useAllotmentBeforeBookings');

		return (
			<CardController
				className="card-borderless"
				mode={this.props.mode}
				icon="user"
				title="Identity">
				<Row gutter={16}>
					<Col xs={24}>
						{this.renderImages("Logo", logo, logoConfig.key, 1024 * 256)}
					</Col>
					<Col xs={24}>
						{this.renderImages("Document Header", header, headerConfig.key, 1024 * 1024)}
					</Col>
					<Col xs={24}>
						{this.renderImages("Document Footer", footer, footerConfig.key, 1024 * 1024)}
					</Col>
					<Col xs={24}>
						{useAllotmentBeforeBookingsConfig == null && <Alert
							showIcon
							message="Warning"
							description="The Use Allotment Before Bookings configuration is missing in database (key useAllotmentBeforeBookings)"
							type="warning"
							className="mb-3"
							closeText={this.renderAddButton("useAllotmentBeforeBookings")}
						/>}
						{useAllotmentBeforeBookingsConfig != null && <BooleanView
							mode={this.props.mode}
							label="Use Allotment Before Bookings"
							value={useAllotmentBeforeBookingsConfig.value === "true"}
							onChanged={(val) => {
								useAllotmentBeforeBookingsConfig.value = val.toString();
							}}
						/>
						}
					</Col>
				</Row>
				<Row gutter={16}>
					<Col xs={24}>
						{this.renderAutoConnectProduct()}
					</Col>
				</Row>
			</CardController>
		);
	}

	private renderRebootWarning() {
		return (<Alert
			showIcon
			message="Warning"
			description="This changes will be effective when the server services reboot. Reboot manually with the the button 'Reboot services' above"
			type="warning"
			className="mb-3"
		/>)
	}
	private renderSequencePrefix() {
		let prefixOptions = this.props.model.items.filter(x => x.key.endsWith('SequencePrefix'));

		let rows = prefixOptions.map((prefixConfig, index) => {
			return <Row gutter={16}>
				<Col xs={24}>
					<TextView
						mode={this.props.mode}
						label={prefixConfig.key}
						value={prefixConfig.value}
						onChanged={newVal => prefixConfig.value = newVal}
					/>
				</Col>
			</Row>
		})
		return <CardController
			className="card-borderless"
			mode={this.props.mode}
			icon=" ordered-list"
			title="Sequence Prefix">
			{rows}
		</CardController>
	}
	private renderRedisCacheUrl() {

		const redisUrl = this.props.model.items.find(x => x.key == 'RedisUrl');
		const redisPort = this.props.model.items.find(x => x.key == 'RedisPort');
		const redistTimeout = this.props.model.items.find(x => x.key == 'RedisTimeout');
		const redisAccessKey = this.props.model.items.find(x => x.key == 'RedisAccessKey');
		const redisSsl = this.props.model.items.find(x => x.key == 'RedisSsl');
		const redisSyncTimeout = this.props.model.items.find(x => x.key == 'RedisSyncTimeout');
		const redisDatabaseId = this.props.model.items.find(x => x.key == 'RedisDatabaseId');


		const cacheType = this.props.model.items.find(x => x.key == 'CacheType');

		if (!redisUrl || !redisPort || !redistTimeout || !redisAccessKey || !redisSsl || !redisSyncTimeout || !redisDatabaseId || !cacheType)
			return <Alert
				showIcon
				message="Warning"
				description="The cache configuration is missing (RedisUrl, RedisPort, RedisTimeout, RedisAccessKey, RedisSsl, RedisSyncTimeout, CacheType or RedisDatabaseId  keys)"
				type="warning"
				className="mb-3"
				closeText={this.renderAddButton("RedisUrl")}
			/>;

		return (
			<CardController
				className="card-borderless"
				mode={this.props.mode}
				icon="database"
				title="Cache Service">
				<Row gutter={24}>
					<Form.Item
						label="Cache mode"
						required={true}
						validateStatus={cacheType.value == null ? 'error' : 'success'}
						help={cacheType.value == null ? 'Required' : undefined}
					>
						<Group
							disabled={this.props.mode == 'display'}
							value={cacheType.value} onChange={e => cacheType.value = e.target.value}
						>
							<Radio value="InMemory">InMemory</Radio>
							<Radio value="Redis">Redis</Radio>
						</Group>
					</Form.Item>
				</Row>
				{this.renderRebootWarning()}


				{cacheType.value === 'Redis' && <React.Fragment> < Row gutter={12}>
					<Col xs={12}>
						<TextView
							mode={this.props.mode}
							label="Redis server host"
							value={redisUrl.value}
							onChanged={newVal => redisUrl.value = newVal}
						/>
					</Col>
					<Col xs={12}>
						<TextView
							mode={this.props.mode}
							label="Redis server port"
							value={redisPort.value}
							onChanged={newVal => redisPort.value = newVal}
						/>
					</Col>
				</Row>
					<Row gutter={12}>
						<Col xs={12}>
							<TextView
								mode={this.props.mode}
								label="Redis server timeout"
								value={redistTimeout.value}
								onChanged={newVal => redistTimeout.value = newVal}
							/>
						</Col>
						<Col xs={12}>
							<TextView
								mode={this.props.mode}
								label="Redis server password"
								value={redisAccessKey.value}
								onChanged={newVal => redisAccessKey.value = newVal}
							/>
						</Col>
					</Row>
					<Row gutter={12}>
						<Col xs={8}>

							<BooleanView
								mode={this.props.mode}
								label="Redis server use ssl"
								value={redisSsl.value === "true"}
								onChanged={(val) => {
									redisSsl.value = val.toString();
								}}
							/>
						</Col>
						<Col xs={8}>
							<TextView
								mode={this.props.mode}
								label="Redis server sync timeout"
								value={redisSyncTimeout.value}
								onChanged={newVal => redisSyncTimeout.value = newVal}
							/>
						</Col>
						<Col xs={8}>
							<TextView
								mode={this.props.mode}
								label="Redis database id"
								value={redisDatabaseId.value}
								onChanged={newVal => redisDatabaseId.value = newVal}
							/>
						</Col>
					</Row>
					<Row>
						<Col xs={8}>
							<Button block={true} onClick={this.props.resetCacheConnection}>Reset connection</Button>
						</Col>
					</Row>
				</React.Fragment>
				}

			</CardController>
		);
	}

	private renderLogConfig() {
		const loggerName = this.props.model.items.find(x => x.key == 'LoggerName');
		const loggerLevel = this.props.model.items.find(x => x.key == 'LoggerLevel');
		if (!loggerName || !loggerLevel) {
			return (
				<Alert
					showIcon
					message="Warning"
					description="The log configuration is missing in database (key SwitchLog)"
					type="warning"
					className="mb-3"
					closeText={this.renderAddButton("LoggerName", "LoggerLevel")}
				/>
			);
		}

		const options = [
			{ value: "Off", label: "Off" },
			{ value: "All", label: "All" },
			{ value: "Debug", label: "Debug" },
			{ value: "Info", label: "Info" },
			{ value: "Error", label: "Error" },
			{ value: "Warn", label: "Warn" },
		];
		return (
			<CardController
				className="card-borderless"
				mode={this.props.mode}
				icon="exception"
				title="Log service config"
			>
				<Row>
					<Col xs={24}>
						{this.renderRebootWarning()}
					</Col>
					<Col xs={24}>
						<Form.Item
							label="Logger type"
							required={true}
							validateStatus={loggerName.value == null ? 'error' : 'success'}
							help={loggerName.value == null ? 'Required' : undefined}
						>
							<Group
								disabled={this.props.mode == 'display'}
								value={loggerName.value}
								onChange={e => loggerName.value = e.target.value}
							>
								<Radio value="file">File</Radio>
								<Radio value="AdoNetAppender">Database</Radio>
								<Radio value="ai">Application Insights</Radio>
							</Group>
						</Form.Item>
					</Col>
					<Col xs={24}>
						<DropdownView
							label="Logger level"
							required={true}
							disabled={this.props.mode == 'display'}
							model={loggerLevel.value}
							onChanged={e => loggerLevel.value = e.value}
							options={options}
						/>
					</Col>
				</Row>
			</CardController >
		);
	}

	private renderTwillioConfig() {
		const twillioAccountId = this.props.model.items.find(x => x.key == 'TAccountId');
		const twillioAccountToken = this.props.model.items.find(x => x.key == 'TAccountToken');
		const smsFromNumber = this.props.model.items.find(x => x.key == 'SmsFromNumber');

		if (!twillioAccountId || !twillioAccountToken || !smsFromNumber)
			return <Alert
				showIcon
				message="Warning"
				description="The twillio service configuration is missing (TAccountId, TAccountToken or SmsFromNumber keys)"
				type="warning"
				className="mb-3"
				closeText={this.renderAddButton("TAccountId", "TAccountToken", "SmsFromNumber")}
			/>;

		return (
			<CardController
				className="card-borderless"
				mode={this.props.mode}
				icon="phone"
				title="Twillio Service Configuration">
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={twillioAccountId.key}
							value={twillioAccountId.value}
							onChanged={newVal => twillioAccountId.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={twillioAccountToken.key}
							value={twillioAccountToken.value}
							onChanged={newVal => twillioAccountToken.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={smsFromNumber.key}
							value={smsFromNumber.value}
							onChanged={newVal => smsFromNumber.value = newVal}
						/>
					</Col>
				</Row>
			</CardController>
		);
	}

	private renderMessageBus() {
		const retriesCount = this.props.model.items.find(x => x.key == 'RetriesCount');
		const minRetryLimit = this.props.model.items.find(x => x.key == 'MinRetryInterval');
		const maxRetryLimit = this.props.model.items.find(x => x.key == 'MaxRetryInterval');
		const retryDelay = this.props.model.items.find(x => x.key == 'RetryIntervalDelay');

		if (!retriesCount || !minRetryLimit || !maxRetryLimit || !retryDelay)
			return <Alert
				showIcon
				message="Warning"
				description="The retry policy configuration is missing (RetriesCount,MaxRetryInterval, MinRetryInterval, RetryIntervalDelay keys )"
				type="warning"
				className="mb-3"
				closeText={this.renderAddButton("RetriesCount")}
			/>;

		return (<React.Fragment>
			{this.renderRebootWarning()}
			<Row gutter={24}>
				<Col xs={12}>
					<TextView
						mode={this.props.mode}
						label={retriesCount.key}
						value={retriesCount.value}
						onChanged={newVal => retriesCount.value = newVal}
					/>
				</Col>
				<Col xs={12}>
					<TextView
						mode={this.props.mode}
						label={retryDelay.key}
						value={retryDelay.value}
						onChanged={newVal => retryDelay.value = newVal}
					/>
				</Col>
			</Row>
			<Row gutter={24}>
				<Col xs={12}>
					<TextView
						mode={this.props.mode}
						label={minRetryLimit.key}
						value={minRetryLimit.value}
						onChanged={newVal => minRetryLimit.value = newVal}
					/>
				</Col>
				<Col xs={12}>
					<TextView
						mode={this.props.mode}
						label={maxRetryLimit.key}
						value={maxRetryLimit.value}
						onChanged={newVal => maxRetryLimit.value = newVal}
					/>
				</Col>
			</Row>
		</React.Fragment>
		);
	}

	renderRabbitConfig() {


		const rabbitUrl = this.props.model.items.find(x => x.key == 'BusRabbitMQUri');
		const rabbitUser = this.props.model.items.find(x => x.key == 'RabbitUsername');
		const rabbitPass = this.props.model.items.find(x => x.key == 'RabbitPassword');

		if (!rabbitUrl || !rabbitUser || !rabbitPass) {
			return (
				<Alert
					showIcon
					message="Warning"
					description="The Quote configuration is missing (BusRabbitMQUri, RabbitUsername, RabbitPassword keys)"
					type="warning"
					className="mb-3"
				/>
			);
		}

		return <Row>
			<Col>
				<TextView
					value={rabbitUrl.value}
					mode={this.props.mode}

					onChanged={e => rabbitUrl.value = e}
					label="Rabbit mq host url"
				/>
			</Col>
			<Col>
				<TextView
					value={rabbitUser.value}
					mode={this.props.mode}
					onChanged={e => rabbitUser.value = e}
					label="Rabbit mq username"
				/>
			</Col>
			<Col>
				<TextView
					value={rabbitPass.value}
					mode={this.props.mode}
					type="password"
					onChanged={e => rabbitPass.value = e}
					label="Rabbit mq password"
				/>
			</Col>
		</Row>;
	}

	renderAzure() {
		const azureNamespace = this.props.model.items.find(x => x.key == 'AzureNamespace');
		const azureServicePath = this.props.model.items.find(x => x.key == 'AzureServicePath');
		const azureSecretKey = this.props.model.items.find(x => x.key == 'AzureSecretKey');
		const azureKeyName = this.props.model.items.find(x => x.key == 'AzureKeyName');

		if (!azureNamespace || !azureServicePath || !azureSecretKey || !azureKeyName) {
			return (
				<Alert
					showIcon
					message="Warning"
					description="The Quote configuration is missing (AzureNamespace, AzureServicePath, AzureSecretKey, AzureKeyName keys)"
					type="warning"
					className="mb-3"
				/>
			);
		}

		return (<React.Fragment><Row>
			{/*<Col>
				<TextView
					value={azureNamespace.value}
					mode={this.props.mode}
					onChanged={e => azureNamespace.value = e}
					label="Azure namespace"
				/>
			</Col>*/}
			<Col>
				<TextView
					value={azureServicePath.value}
					mode={this.props.mode}
					onChanged={e => azureServicePath.value = e}
					label="Azure Service Path"
				/>
			</Col>
		</Row>
			<Row>
				<Col>
					<TextView
						value={azureKeyName.value}
						mode={this.props.mode}
						onChanged={e => azureKeyName.value = e}
						label="Azure key name"
					/>
				</Col>
				<Col>
					<TextView
						value={azureSecretKey.value}
						mode={this.props.mode}
						type="password"
						onChanged={e => azureSecretKey.value = e}
						label="Azure secret shared key"
					/>
				</Col>
			</Row></React.Fragment>);
	}

	private renderBusMessageConfig() {
		const busType = this.props.model.items.find(x => x.key == 'BusType');

		const notificationUrl = this.props.model.items.find(x => x.key == 'BusNotificationUrl');
		const scheduleUrl = this.props.model.items.find(x => x.key == 'BusScheduleUrl');

		if (!busType || !notificationUrl || !scheduleUrl) {
			return (
				<Alert
					showIcon
					message="Warning"
					description="The Quote configuration is missing (BusType, BusNotificationUrl, BusScheduleUrl keys)"
					type="warning"
					className="mb-3"
				/>
			);
		}

		return <CardController
			className="card-borderless"
			mode={this.props.mode}
			title="Bus message configuration">
			<Row gutter={24}>
				<Form.Item
					label="Service BUS type"
					required={true}
					validateStatus={busType.value == null ? 'error' : 'success'}
					help={busType.value == null ? 'Required' : undefined}
				>
					<Group
						disabled={this.props.mode == 'display'}
						value={busType.value}
						onChange={e => busType.value = e.target.value}
					>
						<Radio value="InMemory">In memory queue</Radio>
						<Radio value="RabbitMQ">RabbitMQ</Radio>
						<Radio value="Azure">Azure service BUS</Radio>
					</Group>
				</Form.Item>
			</Row>
			{this.renderMessageBus()}
			<Row>
				<Col>
					<TextView
						value={notificationUrl.value}
						mode={this.props.mode}
						onChanged={e => {
							notificationUrl.value = e;
							this.forceUpdate();

						}}
						label="Notification queue name"
					/>
				</Col>
				<Col>
					<TextView
						value={scheduleUrl.value}
						mode={this.props.mode}
						onChanged={e => scheduleUrl.value = e}
						label="Schedule queue name"
					/>
				</Col>
			</Row>
			{busType.value == "RabbitMQ" && this.renderRabbitConfig()}
			{busType.value == "Azure" && this.renderAzure()}
		</CardController>;
	}

	private renderSmtpEmail() {

		const smtpPort = this.props.model.items.find(x => x.key == 'SmtpPort');
		const smtpHost = this.props.model.items.find(x => x.key == 'SmtpHost');
		const smtpFrom = this.props.model.items.find(x => x.key == 'SmtpFrom');
		const smtpUsername = this.props.model.items.find(x => x.key == 'SmtpUsername');
		const smtpPassword = this.props.model.items.find(x => x.key == 'SmtpPassword');
		const smtpHasSsl = this.props.model.items.find(x => x.key == 'SmtpHasSsl');
		const smtpAdminAddresses = this.props.model.items.find(x => x.key == 'SmtpAdminAddresses');

		if (!smtpPort || !smtpHost || !smtpFrom || !smtpUsername || !smtpPassword || !smtpHasSsl || !smtpAdminAddresses)
			return <Alert
				showIcon
				message="Warning"
				description="The smtp email server configuration is missing (SmtpPort, SmtpHost, SmtpAdminAddresses, SmtpHasSsl, SmtpPassword, SmtpUsername or SmtpFrom keys)"
				type="warning"
				className="mb-3"
				closeText={this.renderAddButton("SmtpPort", "SmtpHost", "SmtpFrom", "SmtpUsername", "SmtpPassword", "SmtpHasSsl", "SmtpAdminAddresses")}
			/>;

		return (
			<CardController
				className="card-borderless"
				mode={this.props.mode}
				icon="mail"
				title="Email Server">
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={smtpHost.key}
							value={smtpHost.value}
							onChanged={newVal => smtpHost.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={smtpFrom.key}
							value={smtpFrom.value}
							onChanged={newVal => smtpFrom.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={smtpPort.key}
							value={smtpPort.value}
							onChanged={newVal => smtpPort.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={smtpUsername.key}
							value={smtpUsername.value}
							onChanged={newVal => smtpUsername.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={smtpPassword.key}
							value={smtpPassword.value}
							type="password"
							onChanged={newVal => smtpPassword.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={smtpAdminAddresses.key}
							value={smtpAdminAddresses.value}
							onChanged={newVal => smtpAdminAddresses.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<BooleanView
							mode={this.props.mode}
							label={smtpHasSsl.key}
							value={smtpHasSsl.value != null ? smtpHasSsl.value.toLowerCase() === "true" : false}
							onChanged={newVal => smtpHasSsl.value = newVal.toString()}
						/>
					</Col>
				</Row>

			</CardController>
		);
	}

	private renderStorage() {
		const StorageConnectionString = this.props.model.items.find(x => x.key == 'StorageConnectionString');
		const StorageDir = this.props.model.items.find(x => x.key == 'Storage:Dir');
		const DocsContainer = this.props.model.items.find(x => x.key == 'DocsContainer');
		const RSAModulusConfig = this.props.model.items.find(x => x.key == 'RSAModulus');

		return (
			<CardController
				className="card-borderless"
				mode={this.props.mode}
				icon="hdd"
				title="Storage">
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={StorageConnectionString.key}
							value={StorageConnectionString.value}
							onChanged={newVal => StorageConnectionString.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={StorageDir.key}
							value={StorageDir.value}
							onChanged={newVal => StorageDir.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={DocsContainer.key}
							value={DocsContainer.value}
							onChanged={newVal => DocsContainer.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={RSAModulusConfig.key}
							value={RSAModulusConfig.value}
							onChanged={newVal => RSAModulusConfig.value = newVal}
						/>
					</Col>
				</Row>
			</CardController>
		);
	}

	private renderPaysafe() {
		const PaysafeEnvironment = this.props.model.items.find(x => x.key == 'PaysafeEnvironment');
		const PaysafeApiBaseUrl = this.props.model.items.find(x => x.key == 'PaysafeApiBaseUrl');
		const PaysafeScriptUrl = this.props.model.items.find(x => x.key == 'PaysafeScriptUrl');
		const PaysafeClientApiKey = this.props.model.items.find(x => x.key == 'PaysafeClientApiKey');
		const PaysafeSecretApiKey = this.props.model.items.find(x => x.key == 'PaysafeSecretApiKey');
		const PaysafeDefaultMerchantAccountId = this.props.model.items.find(x => x.key == 'PaysafeDefaultMerchantAccountId');
		const PaysafeEnabled = this.props.model.items.find(x => x.key == 'PaysafeEnabled');

		if (!PaysafeEnvironment || !PaysafeApiBaseUrl || !PaysafeScriptUrl || 
			!PaysafeClientApiKey || !PaysafeSecretApiKey || !PaysafeDefaultMerchantAccountId || !PaysafeEnabled)
			return <Alert
				showIcon
				message="Warning"
				description="The Paysafe payment gateway configuration is missing (PaysafeEnvironment, PaysafeApiBaseUrl, PaysafeScriptUrl, PaysafeClientApiKey, PaysafeSecretApiKey, PaysafeDefaultMerchantAccountId or PaysafeEnabled keys)"
				type="warning"
				className="mb-3"
				closeText={this.renderAddButton("PaysafeEnvironment", "PaysafeApiBaseUrl", "PaysafeScriptUrl", "PaysafeClientApiKey", "PaysafeSecretApiKey", "PaysafeDefaultMerchantAccountId", "PaysafeEnabled")}
			/>;

		return (
			<React.Fragment>
				<Row>
					<Col xs={24}>
						<BooleanView
							mode={this.props.mode}
							label="Is enabled"
							value={PaysafeEnabled.value != null ? PaysafeEnabled.value.toLowerCase() === "true" : false}
							onChanged={newVal => PaysafeEnabled.value = newVal.toString()}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={PaysafeEnvironment.key}
							value={PaysafeEnvironment.value}
							onChanged={newVal => PaysafeEnvironment.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={PaysafeApiBaseUrl.key}
							value={PaysafeApiBaseUrl.value}
							onChanged={newVal => PaysafeApiBaseUrl.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={PaysafeScriptUrl.key}
							value={PaysafeScriptUrl.value}
							onChanged={newVal => PaysafeScriptUrl.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={PaysafeClientApiKey.key}
							value={PaysafeClientApiKey.value}
							onChanged={newVal => PaysafeClientApiKey.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={PaysafeSecretApiKey.key}
							value={PaysafeSecretApiKey.value}
							onChanged={newVal => PaysafeSecretApiKey.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={PaysafeDefaultMerchantAccountId.key}
							value={PaysafeDefaultMerchantAccountId.value}
							onChanged={newVal => PaysafeDefaultMerchantAccountId.value = newVal}
						/>
					</Col>
				</Row>
			</React.Fragment>
		);
	}

	private renderSagePay() {

		const SagePayVendorName = this.props.model.items.find(x => x.key == 'SagePayVendorName');
		const SagePayIntegrationKey = this.props.model.items.find(x => x.key == 'SagePayIntegrationKey');
		const SagePayIntegrationPassword = this.props.model.items.find(x => x.key == 'SagePayIntegrationPassword');
		const SagePayUrl = this.props.model.items.find(x => x.key == 'SagePayUrl');
		const SagePayScriptUrl = this.props.model.items.find(x => x.key == 'SagePayScriptUrl');
		const SagePayEnabled = this.props.model.items.find(x => x.key == 'SagePayEnabled');

		if (!SagePayVendorName || !SagePayIntegrationKey || !SagePayIntegrationPassword || !SagePayUrl || !SagePayScriptUrl || !SagePayEnabled)
			return <Alert
				showIcon
				message="Warning"
				description="The sage pay payment gateway configuration is missing (SagePayVendorName, SagePayIntegrationKey, SagePayIntegrationPassword, SagePayScriptUrl, SagePayEnabled or SagePayUrl keys)"
				type="warning"
				className="mb-3"
				closeText={this.renderAddButton("SagePayVendorName", "SagePayIntegrationKey", "SagePayIntegrationPassword", "SagePayUrl")}
			/>;

		return (
			<React.Fragment>
				<Row>
					<Col xs={24}>
						<BooleanView
							mode={this.props.mode}
							label="Is enabled"
							value={SagePayEnabled.value != null ? SagePayEnabled.value.toLowerCase() === "true" : false}
							onChanged={newVal => SagePayEnabled.value = newVal.toString()}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={SagePayVendorName.key}
							value={SagePayVendorName.value}
							onChanged={newVal => SagePayVendorName.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={SagePayIntegrationKey.key}
							value={SagePayIntegrationKey.value}
							onChanged={newVal => SagePayIntegrationKey.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={SagePayIntegrationPassword.key}
							value={SagePayIntegrationPassword.value}
							onChanged={newVal => SagePayIntegrationPassword.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={SagePayUrl.key}
							value={SagePayUrl.value}
							onChanged={newVal => SagePayUrl.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={"Sagepay iframe scripts url"}
							value={SagePayScriptUrl.value}
							onChanged={newVal => SagePayScriptUrl.value = newVal}
						/>
					</Col>
				</Row>
			</React.Fragment>
		);
	}
	private renderCheckout() {

		const CheckoutPublicKey = this.props.model.items.find(x => x.key == 'CheckoutPublicKey');
		const CheckoutSecretKey = this.props.model.items.find(x => x.key == 'CheckoutSecretKey');
		const CheckoutScriptUrl = this.props.model.items.find(x => x.key == 'CheckoutScriptUrl');
		const CheckoutUrl = this.props.model.items.find(x => x.key == 'CheckoutUrl');
		const CheckoutSandBox = this.props.model.items.find(x => x.key == 'CheckoutSandBox');
		const CheckoutEnabled = this.props.model.items.find(x => x.key == 'CheckoutEnabled');



		if (!CheckoutPublicKey || !CheckoutSecretKey || !CheckoutScriptUrl || !CheckoutUrl || !CheckoutSandBox || !CheckoutEnabled)
			return <Alert
				showIcon
				message="Warning"
				description="The checkout payment gateway configuration is missing"
				type="warning"
				className="mb-3"
			/>;

		return (
			<React.Fragment>
				<Row>
					<Col xs={24}>
						<BooleanView
							mode={this.props.mode}
							label="Is enabled"
							value={CheckoutEnabled.value != null ? CheckoutEnabled.value.toLowerCase() === "true" : false}
							onChanged={newVal => CheckoutEnabled.value = newVal.toString()}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={CheckoutPublicKey.key}
							value={CheckoutPublicKey.value}
							onChanged={newVal => CheckoutPublicKey.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={CheckoutSecretKey.key}
							value={CheckoutSecretKey.value}
							onChanged={newVal => CheckoutSecretKey.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={CheckoutUrl.key}
							value={CheckoutUrl.value}
							onChanged={newVal => CheckoutUrl.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<TextView
							mode={this.props.mode}
							label={"Checkout iframe script url"}
							value={CheckoutScriptUrl.value}
							onChanged={newVal => CheckoutScriptUrl.value = newVal}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<BooleanView
							mode={this.props.mode}
							label={CheckoutSandBox.key}
							value={CheckoutSandBox.value != null ? CheckoutSandBox.value.toLowerCase() === "true" : false}
							onChanged={newVal => CheckoutSandBox.value = newVal.toString()}
						/>
					</Col>
				</Row>

			</React.Fragment>
		);
	}

	private renderGatewayConfig() {

		return <CardController
			className="card-borderless"
			mode={this.props.mode}
			title="Card payment gateway configuration">
			<Tabs>
				<TabPane key="paysafe" tab="Paysafe">
					{this.renderPaysafe()}
				</TabPane>
				<TabPane key="sagepay" tab="Sagepay">
					{this.renderSagePay()}
				</TabPane>
				<TabPane key="checkout" tab="Checkout">
					{this.renderCheckout()}
				</TabPane>
			</Tabs>
		</CardController>;
	}

	getLogColor = (level: number) => {

		switch (level) {
			case 1:
				return 'grey';
			case 2:
				return 'red';
			case 3:
				return 'red';
			case 4:
				return 'blue';
			case 5:
				return 'orange';
		}

		return 'black';
	}

	renderLogs = () => {
		return (<CardController
			className="card-borderless"
			mode={this.props.mode}
			title="Real time logging">
			<div style={{ maxHeight: 800, overflow: 'auto', padding: '5px' }}>
				{
					this.props.logs.map((item, index) => {
						return (<p style={{ color: this.getLogColor(item.level) }} key={'log' + index}>[{item.date.toString()}]: {item.message}</p>);
					})
				}
			</div>
		</CardController>)
	}

	public render() {
		if (this.props.model == null) {
			return null;
		}

		return (
			<div className="window window-normal">
				<Prompt
					when={this.props.model.isDirty}
					message={location => `If you go to ${location.pathname}, all changes will be lost. Are you sure you want to continue?`}
				/>
				<Row>
					<Col xs={24} xl={{ offset: 3, span: 18 }}>
						<Row>
							<Col xs={24}>
								<HeaderView
									title={"System Configurations"}
									mode={this.props.mode}
									onModeChanged={this.props.onModeChanged}
									save={this.props.save}
									cancel={this.props.cancel}
									contextService={this.props.model}
								/>
							</Col>
						</Row>
						<div className="card p-3">
							{/*<Button onClick={this.props.downloadLogs} style={{marginRight: '10px'}} type="primary">Download logs</Button>*/}
							<Button onClick={this.props.reboot} type="danger">Reboot server</Button>
							<Divider />
							<Row gutter={24}>
								<Col xs={24} lg={12}>
									{this.renderIdentity()}
									{this.renderSequencePrefix()}
									{this.renderRedisCacheUrl()}
									{this.renderGatewayConfig()}
									{this.renderLogConfig()}
									{this.renderBusMessageConfig()}
								</Col>
								<Col xs={24} lg={12}>
									{this.renderStorage()}
									{this.renderSmtpEmail()}
									{this.renderTwillioConfig()}
									{this.renderLogs()}
									{this.renderNotifications()}
								</Col>
							</Row>
						</div>
					</Col>
				</Row>
			</div>
		);
	}

	private renderAddButton = (...key: string[]) => {
		return (
			<Button
				disabled={this.props.mode === "display"}
				icon="plus"
				ghost={true}
				shape="circle-outline"
				type="ghost"
				onClick={this.addKey(key)}
			/>
		);
	}

	private addKey = (key: string[]) => () => this.props.add(key);
}



// WEBPACK FOOTER //
// ./src/app/general/config-option/form/config-option-form.view.tsx